/**
 * Zustand der Anmeldung.
 *
 * Beim Start wird gefragt, ob die Einrichtung offen ist und wer angemeldet ist.
 * Alle Seiten greifen auf diesen Zustand zu, statt selbst zu prüfen.
 */
import { create } from 'zustand';
import { api } from '../api/client.ts';
import type { CurrentUser } from '../api/types.ts';
import { clearOfflineData } from '../lib/offline.ts';
import { clearOutbox } from './outbox.ts';

/** Antwort von /api/auth/status. */
interface AuthStatus {
  setupRequired: boolean;
  user: CurrentUser | null;
}

interface AuthState {
  /** Läuft gerade eine Abfrage? */
  loading: boolean;
  /** Ist die Einrichtung noch offen? */
  setupRequired: boolean;
  user: CurrentUser | null;
  error: string | null;
  refresh: () => Promise<void>;
  setup: (input: { username: string; displayName: string; password: string }) => Promise<void>;
  login: (input: { username: string; password: string }) => Promise<void>;
  logout: () => Promise<void>;
  changePassword: (input: { currentPassword: string; newPassword: string }) => Promise<void>;
}

export const useAuth = create<AuthState>((set) => ({
  loading: true,
  setupRequired: false,
  user: null,
  error: null,

  async refresh() {
    set({ loading: true, error: null });
    try {
      const status = await api.get<AuthStatus>('/api/auth/status');
      set({ setupRequired: status.setupRequired, user: status.user, loading: false });
    } catch {
      set({ loading: false, error: 'Der Server ist nicht erreichbar.' });
    }
  },

  async setup(input) {
    await api.post('/api/auth/setup', input);
    const status = await api.get<AuthStatus>('/api/auth/status');
    set({ setupRequired: status.setupRequired, user: status.user, error: null });
  },

  async login(input) {
    await api.post('/api/auth/login', input);
    const status = await api.get<AuthStatus>('/api/auth/status');
    set({ user: status.user, setupRequired: status.setupRequired, error: null });
  },

  async logout() {
    await api.post('/api/auth/logout');
    // Nichts von dieser Person bleibt im Gerät: zwischengespeicherte Daten,
    // Medien und noch nicht übertragene Änderungen.
    clearOutbox();
    await clearOfflineData();
    set({ user: null });
  },

  async changePassword(input) {
    await api.post('/api/auth/password', input);
    const status = await api.get<AuthStatus>('/api/auth/status');
    set({ user: status.user, error: null });
  },
}));
